import React, { useState, useEffect, useMemo, useRef } from 'react';
import './App.css';
import mxoDevLogo from './assets/mxo-dev-logo.webp';
import mxoDevLogoGif from './assets/mxo-dev-logo.gif';
import {
  Header,
  NavBar,
  FilterBar,
  EventsTable,
  ScheduledTable,
  EventModal,
  NotificationPrompt,
} from './components';
import AbbreviationsModal from './components/AbbreviationsModal';
import DeferredTable from './components/DeferredTable';
import LoginScreen from './components/LoginScreen';
import TriageSection, { TriageEventDraft } from './components/TriageSection';
import { UnscheduledEvent, FilterOptions } from './types';
import {
  EVENT_STATUS,
  PROJECTED_ERTS_REFRESH_INTERVAL_MS,
  ACTIVE_EVENTS_REFRESH_INTERVAL_MS,
  EDIT_LOCK_HEARTBEAT_INTERVAL_MS,
  HISTORY_WINDOW_OPTIONS,
  DEFAULT_HISTORY_WINDOW_DAYS,
  DEFECT_HISTORY_LOOKBACK_DAYS,
  TRIAGE_ENABLED,
  TRIAGE_REFRESH_INTERVAL_MS,
} from './constants';
import { useAuth, aliasFromEmail } from './auth';
import { eventHasIropCode, isIropCode } from './utils/iropUtils';
import { computeHistoryFilterOptions, eventAtaChapters } from './utils/historyFilterOptions';
import { todayDateZulu, dateZuluOf } from './utils/dateUtils';
import {
  fetchActiveEvents,
  fetchCompletedEvents,
  fetchEventById,
  createEvent,
  updateEvent,
} from './services/eventService';
import { clearActiveEvents, ClearActiveEventsResult } from './services/clear-active-events';
import { API_URL } from './services/apiClient';
import {
  concatNotesForSummary,
  generateAndStoreSummaryInBackground,
} from './services/summaryService';
import { generateSkyWrenchForEvent } from './services/skyWrenchService';
import {
  acquireEditLock,
  refreshEditLock,
  releaseEditLock,
  fetchActiveLocks,
  isValidEditLockEventId,
  AcquireLockResult,
} from './services/lockService';
import { createLogger } from './utils/logger';
import {
  fetchActiveErtsPredictions,
  requestErtsPrediction,
  ErtsPredictionRequestResult,
  ErtsPredictionSummary,
} from './services/ertsPredictionService';
import { fetchAtaCodes } from './services/ataCodeService';
import { refreshAbbreviationStore } from './services/abbreviationService';
import {
  getEventsTimeAlertState,
  getEventTimeAlertState,
  mergeTimeAlertStates,
  TimeAlertState,
} from './utils/eventAlerts';
import { getSidebarFilteredEvents } from './utils/sidebarFilters';
import { useAlertFavicon } from './utils/useAlertFavicon';
import { byCarrierThenTail, applyProjectedErts, mergeEvents } from './utils/eventTransforms';
import { aggregateDeferredDueState, DeferredDueState } from './utils/deferredAlerts';
import { commitTriageItem, fetchPendingTriage, TriageItem } from './services/triageService';

const logger = createLogger('App');
const PREDICTION_STATUS_DURATION_MS: number = 8_000;
const CLEARABLE_RECORD_TYPES: Record<string, string> = {
  unscheduled: 'Unscheduled',
  planned: 'Planned',
  deferred: 'Deferred',
};
const CLEAR_ALL_ENVIRONMENT_PATTERN: RegExp = /\/(?:beta|gamma)\/?$/i;
const CLEAR_ALL_ENABLED: boolean = CLEAR_ALL_ENVIRONMENT_PATTERN.test(API_URL);

/** Metadata retained while a reviewed Triage item is completed in EventModal. */
interface PendingTriageCommit {
  triageId: string;
  version: number;
  action: 'CREATE' | 'UPDATE';
  ertsAction: TriageEventDraft['ertsAction'];
  ertsExistedBefore: boolean;
  /** Defects that receive an immediate Knowledge Base ATA recommendation. */
  recommendationDefectIds: number[];
  verificationFields: {
    status: boolean;
    timeDeclaredDate: boolean;
    timeDeclaredTime: boolean;
  };
}

/** A prediction verified against the exact event revision that produced it. */
interface VerifiedPrediction {
  revision: number;
  prediction: ErtsPredictionSummary;
}

/** Returns the optimistic-lock revision used by durable ERTS predictions. */
function getEventRevision(event: UnscheduledEvent): number {
  return event.version ?? event.revisionCounter;
}

/** Returns only verified predictions that match the currently loaded event revision. */
function getMatchingVerifiedPredictions(
  currentEvents: UnscheduledEvent[],
  verifiedPredictions: Record<string, VerifiedPrediction>,
): Record<string, ErtsPredictionSummary> {
  const matchingPredictions: Record<string, ErtsPredictionSummary> = {};
  for (const currentEvent of currentEvents) {
    const verifiedPrediction: VerifiedPrediction | undefined =
      verifiedPredictions[currentEvent.eventId];
    if (verifiedPrediction?.revision === getEventRevision(currentEvent)) {
      matchingPredictions[currentEvent.eventId] = verifiedPrediction.prediction;
    }
  }
  return matchingPredictions;
}

const DEFAULT_FILTERS: FilterOptions = {
  carrier: '',
  aircraft: '',
  station: '',
  irop: '',
  status: '',
  spare: '',
  eventType: '',
  ataChapter: '',
  rtsDate: '',
};

const STORAGE_KEY = 'aether-events';

/** Upper bound (in days) on the completed-history load. The History window
 *  dropdown drives how many days are actually fetched (defaulting to
 *  DEFAULT_HISTORY_WINDOW_DAYS), but the request is always clamped to this cap so
 *  a load never exceeds the largest selectable window. The view shows events
 *  COMPLETED within the window (the backend filters uniformly on
 *  completionTimeZulu across all four event tables; see listEvents/queryEventTable).
 *  Measured payload for the current beta data at 180d is ~4.9MB (list projection),
 *  under the API Gateway 6MB response cap but with limited headroom — as completed
 *  volume grows this single-response load must be replaced with server-side pagination. */
const HISTORY_WINDOW_DAYS = Math.max(...HISTORY_WINDOW_OPTIONS);

/** Rows rendered per page in the History view to keep first paint fast. */
const HISTORY_PAGE_SIZE = 100;

/**
 * Returns the cutoff date (YYYY-MM-DD, UTC) for the history window — used as the
 * server-side `since` filter so only completed records within the selected
 * window are fetched. The requested window is clamped to [1, HISTORY_WINDOW_DAYS]
 * so a load never exceeds the largest selectable window.
 */
function historySinceDate(days: number): string {
  const boundedDays = Math.min(Math.max(days, 1), HISTORY_WINDOW_DAYS);
  const cutoff = new Date(Date.now() - boundedDays * 24 * 60 * 60 * 1000);
  return cutoff.toISOString().split('T')[0];
}

/**
 * Clears any previously cached events from localStorage. The API is now the
 * source of truth; this removes stale mock/cached data left by older builds.
 */
function clearPersistedEvents(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Ignore storage errors
  }
}

/**
 * Maps a time-field alert state to its sidebar/nav highlight class. Red
 * (overdue) takes precedence over yellow (warning). When `muted` is true the
 * static color-only variant is returned so the alert color still shows but the
 * flashing animation is suppressed.
 */
function alertFlashClass(state: TimeAlertState, muted: boolean = false): string {
  if (state === 'overdue') return muted ? 'alert-muted-red' : 'alert-flash-red';
  if (state === 'warning') return muted ? 'alert-muted-yellow' : 'alert-flash-yellow';
  return '';
}

/**
 * Maps the Deferred Events due state to its sidebar highlight class. When
 * `muted` is true the static color-only variant is returned so the color is
 * preserved but the flashing animation is suppressed.
 */
function deferredFlashClass(state: DeferredDueState, muted: boolean = false): string {
  if (state === 'red') return muted ? 'sidebar-link-muted-red' : 'sidebar-link-flash-red';
  if (state === 'amber') return muted ? 'sidebar-link-muted-amber' : 'sidebar-link-flash-amber';
  return '';
}

/**
 * Root application component for AETHER.
 */
const App: React.FC = () => {
  const { isAuthenticated, isLoading, canEdit, canManageAbbreviations, user } = useAuth();
  const currentUserAlias = aliasFromEmail(user?.email);
  const [activeTab, setActiveTab] = useState<string>('homepage');
  const activeTabRef = useRef<string>('homepage');
  activeTabRef.current = activeTab;
  const [filters, setFilters] = useState<FilterOptions>(DEFAULT_FILTERS);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [modalEvent, setModalEvent] = useState<UnscheduledEvent | null>(null);
  const [modalMode, setModalMode] = useState<'view' | 'edit' | 'create'>('view');
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [newRecordType, setNewRecordType] = useState<string>('');

  // Edit-lock state. `lockOwners` maps eventId → alias of the user currently
  // editing it (refreshed by the active-events poll) so other users' Edit
  // buttons are disabled. `lockedEventIdRef` tracks the lock THIS tab holds so
  // it can be released on modal close/save and heartbeated while open.
  const [lockOwners, setLockOwners] = useState<Record<string, string>>({});
  const lockedEventIdRef = useRef<string | null>(null);

  // Holds the source record of an in-progress Move while its pre-populated
  // Create Event window is open. The source is only closed (COMPLETED + moveStatus)
  // once the destination is actually created — cancelling the create leaves the
  // original record untouched.
  const pendingMoveSourceRef = useRef<UnscheduledEvent | null>(null);

  // Alert mute state. While muted, the sidebar/nav buttons keep their alert
  // color but stop flashing. `mutedAlertKeys` records which alerts were active
  // at mute time so a later, genuinely new notification can re-enable flashing.
  const [alertsMuted, setAlertsMuted] = useState<boolean>(false);
  const [mutedAlertKeys, setMutedAlertKeys] = useState<Set<string>>(new Set());

  const [events, setEvents] = useState<UnscheduledEvent[]>([]);
  const [clearAllBusy, setClearAllBusy] = useState<boolean>(false);
  const [predictionStatus, setPredictionStatus] = useState<string | null>(null);
  const [predictionRequestsInFlight, setPredictionRequestsInFlight] = useState<number>(0);
  const verifiedPredictionsRef = useRef<Record<string, VerifiedPrediction>>({});
  const latestPredictionRevisionRef = useRef<Record<string, number>>({});
  const latestPredictionStatusRef = useRef<number>(0);
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
  // True once the first active-events load has completed. The "Updating…"
  // spinner is shown ONLY for that initial load; every subsequent poll,
  // tab-return catch-up, and post-save refresh updates silently.
  const hasLoadedOnceRef = useRef<boolean>(false);
  const [historyEvents, setHistoryEvents] = useState<UnscheduledEvent[]>([]);
  // Becomes true once a completed-history request has settled, so the modal never
  // infers "no related defects" while the corpus is still loading. A failed
  // request still resolves: the picker can only offer the sentinel in that state,
  // and automatic selection must mirror the options actually available.
  const [completedHistoryResolved, setCompletedHistoryResolved] = useState<boolean>(false);
  const completedHistoryLoadingRef = useRef<boolean>(false);
  // Selected History-window size (days). Controls the History tab filter; the
  // fetch retains the 180-day Defect History corpus even when this is smaller.
  const [historyWindowDays, setHistoryWindowDays] = useState<number>(DEFAULT_HISTORY_WINDOW_DAYS);
  // Mirrors historyWindowDays so background refreshes (polling, post-save) read
  // the current window without needing to be re-created on every change.
  const historyWindowDaysRef = useRef<number>(DEFAULT_HISTORY_WINDOW_DAYS);
  historyWindowDaysRef.current = historyWindowDays;
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(false);
  const [sidebarTab, setSidebarTab] = useState<string>('unscheduled');
  // Desktop-notification opt-in prompt: shown on login whenever browser
  // notification permission has not been granted (re-checked every login).
  const [showNotificationPrompt, setShowNotificationPrompt] = useState<boolean>(false);
  // Abbreviations admin panel (MXO-L6 only) open state.
  const [isAbbreviationsOpen, setIsAbbreviationsOpen] = useState<boolean>(false);

  // Triage is polled globally (not only while its page is mounted) so the nav
  // can notify users of new parsed emails. `unseenTriageIds` drives flashing;
  // any non-empty real list keeps the nav label green/bold until cleared.
  const [triageItems, setTriageItems] = useState<TriageItem[]>([]);
  const [triageIsMock, setTriageIsMock] = useState<boolean>(false);
  const [triageLoading, setTriageLoading] = useState<boolean>(true);
  const [unseenTriageIds, setUnseenTriageIds] = useState<Set<string>>(new Set());
  const knownTriageIdsRef = useRef<Set<string>>(new Set());
  const pendingTriageCommitRef = useRef<PendingTriageCommit | null>(null);
  const triageCommitInFlightRef = useRef<boolean>(false);

  // Load active events (IN_WORK/PENDING) for the homepages, sidebar, and nav alerts.
  // The API filters and projects server-side so this payload stays small as data grows.
  // The per-event projected ERTS (from the prediction pipeline) is overlaid so the
  // homepage Projected ERTS column reflects the latest projection per record.
  const reloadActiveEvents = (): void => {
    // The "Updating…" spinner is shown ONLY on the very first load. Once the
    // initial data is in, all later refreshes (the ~2s poll, tab-return
    // catch-up, post-save reloads) update silently — mergeEvents keeps unchanged
    // row references so the table never flashes.
    const firstLoad = !hasLoadedOnceRef.current;
    const spinnerTimer = firstLoad
      ? window.setTimeout(() => setIsRefreshing(true), 400)
      : undefined;
    const done = (): void => {
      if (spinnerTimer !== undefined) window.clearTimeout(spinnerTimer);
      if (firstLoad) setIsRefreshing(false);
      hasLoadedOnceRef.current = true;
    };
    Promise.all([fetchActiveEvents(), fetchActiveErtsPredictions(), fetchActiveLocks()]).then(
      ([apiEvents, predictions, locks]) => {
        // Keep the last good data on a failed fetch (null) instead of blanking
        // the table; only replace when a real result (including an empty list)
        // arrives. Predictions failing returns {} and is harmless.
        if (apiEvents !== null) {
          const matchingVerifiedPredictions: Record<string, ErtsPredictionSummary> =
            getMatchingVerifiedPredictions(apiEvents, verifiedPredictionsRef.current);
          const activePredictions: Record<string, ErtsPredictionSummary> = {
            ...predictions,
            ...matchingVerifiedPredictions,
          };
          const next = applyProjectedErts(apiEvents, activePredictions);
          // Reconcile against current state so an unchanged poll (the common
          // case at ~2s) is a no-op: mergeEvents returns the same references for
          // unchanged rows (and the same array when nothing changed), so React
          // skips re-rendering and the table doesn't flash. Only changed rows
          // update, silently.
          setEvents((current) => mergeEvents(current, next));
        }
        // Refresh the edit-lock ownership map so other users' Edit buttons
        // reflect who is currently editing each event.
        const owners: Record<string, string> = {};
        for (const lock of locks) {
          // Defense in depth: a malformed/stale lock can never become a key in
          // the ownership map, even if a future caller bypasses lockService.
          if (isValidEditLockEventId(lock.event_id)) {
            owners[lock.event_id] = lock.editor_alias;
          }
        }
        setLockOwners(owners);
        done();
      },
      done,
    );
  };

  // Refresh only the projected-ERTS overlay (not the full event list). Used by
  // the polling timer so new projections from the pipeline appear on the
  // homepage automatically without a heavier full reload. Merges onto whatever
  // events are currently loaded via a functional update to avoid stale closures.
  const refreshProjectedErts = (): void => {
    fetchActiveErtsPredictions().then((predictions) => {
      // mergeEvents keeps unchanged row references so unchanged rows don't
      // re-render (no flash); only rows whose projection changed update.
      setEvents((current: UnscheduledEvent[]): UnscheduledEvent[] => {
        const matchingVerifiedPredictions: Record<string, ErtsPredictionSummary> =
          getMatchingVerifiedPredictions(current, verifiedPredictionsRef.current);
        const activePredictions: Record<string, ErtsPredictionSummary> = {
          ...predictions,
          ...matchingVerifiedPredictions,
        };
        return mergeEvents(current, applyProjectedErts(current, activePredictions));
      });
    });
  };

  // Fetches completed history for the greater of the selected History-window
  // size and the Defect History lookback. A null result keeps the last good
  // data rather than blanking it.
  const loadCompletedEvents = (): void => {
    if (completedHistoryLoadingRef.current) return;
    completedHistoryLoadingRef.current = true;
    fetchCompletedEvents(
      historySinceDate(Math.max(historyWindowDaysRef.current, DEFECT_HISTORY_LOOKBACK_DAYS)),
    )
      .then((apiEvents) => {
        if (apiEvents !== null) setHistoryEvents(apiEvents);
      })
      .finally(() => {
        completedHistoryLoadingRef.current = false;
        setCompletedHistoryResolved(true);
      });
  };

  /** Applies a Triage poll and records newly-arrived real item ids as unseen. */
  const applyTriageResult = (items: TriageItem[], isMock: boolean): void => {
    const nextIds = new Set(items.map((item) => item.triage_id));
    const priorIds = knownTriageIdsRef.current;
    setTriageItems(items);
    setTriageIsMock(isMock);
    setTriageLoading(false);
    setUnseenTriageIds((current) => {
      const next = new Set(Array.from(current).filter((id) => nextIds.has(id)));
      if (!isMock && activeTabRef.current !== 'triage') {
        Array.from(nextIds).forEach((id) => {
          if (!priorIds.has(id)) next.add(id);
        });
      }
      return next;
    });
    knownTriageIdsRef.current = nextIds;
  };

  /** Removes a handled Triage item immediately; the next poll confirms it. */
  const removeTriageItem = (triageId: string): void => {
    setTriageItems((current) => current.filter((item) => item.triage_id !== triageId));
    setUnseenTriageIds((current) => {
      const next = new Set(current);
      next.delete(triageId);
      return next;
    });
  };

  useEffect(() => {
    // Wait until authentication is fully established before hitting the API.
    // Firing during the OAuth /callback (before the token exchange completes)
    // produces a 401 that redirects back to login — an app→IdP→app flash loop.
    if (!isAuthenticated) return;
    clearPersistedEvents();
    reloadActiveEvents();
  }, [isAuthenticated]);

  // Poll pending parsed-email items globally every 15 seconds. This effect lives
  // in App rather than TriageSection, so arrivals are detected even while the
  // user is on another page and can drive the navigation notification.
  useEffect(() => {
    if (!TRIAGE_ENABLED || !isAuthenticated) return undefined;
    let active = true;
    const refresh = (): void => {
      fetchPendingTriage().then((result) => {
        if (active) applyTriageResult(result.items, result.isMock);
      });
    };
    refresh();
    const intervalId = window.setInterval(refresh, TRIAGE_REFRESH_INTERVAL_MS);
    return () => {
      active = false;
      window.clearInterval(intervalId);
    };
  }, [isAuthenticated]);

  // Poll the projected-ERTS overlay on the pipeline's inference cadence so the
  // homepage Projected ERTS column updates automatically as predictions change.
  useEffect(() => {
    const intervalId = window.setInterval(refreshProjectedErts, PROJECTED_ERTS_REFRESH_INTERVAL_MS);
    return () => window.clearInterval(intervalId);
  }, []);

  // Poll the active event LIST on a ~2s cadence so new events, status changes,
  // and edits by other users appear automatically (per docs/ARCHITECTURE.md
  // §2 read path). The overlay poll above only re-scores rows already in state;
  // it can't add or remove rows, so the list needs its own poll.
  //
  // Tab-visibility gated: the interval runs only while the tab is visible and is
  // torn down when hidden (backgrounded tabs stop polling). On becoming visible
  // again we refetch immediately so the user sees current data without waiting
  // for the next tick or a manual refresh. authedFetch transparently renews an
  // expired token, and reloadActiveEvents' null-guard keeps the last good data
  // if a poll fails — so a transient error never blanks the table.
  useEffect(() => {
    if (!isAuthenticated) return undefined;

    let intervalId: number | undefined;
    const startPolling = (): void => {
      if (intervalId !== undefined) return;
      intervalId = window.setInterval(reloadActiveEvents, ACTIVE_EVENTS_REFRESH_INTERVAL_MS);
    };
    const stopPolling = (): void => {
      if (intervalId === undefined) return;
      window.clearInterval(intervalId);
      intervalId = undefined;
    };

    const handleVisibility = (): void => {
      if (document.visibilityState === 'visible') {
        // Catch up immediately on return, then resume the 2s cadence. Also
        // refresh completed history (used by History/Completed tabs + modal).
        reloadActiveEvents();
        loadCompletedEvents();
        startPolling();
      } else {
        stopPolling();
      }
    };

    // Start in whatever state the tab currently is.
    if (document.visibilityState === 'visible') startPolling();
    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('focus', handleVisibility);
    return () => {
      stopPolling();
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('focus', handleVisibility);
    };
  }, [isAuthenticated]);

  // Load completed history on mount, and re-load whenever the selected History
  // window changes so only that many days are fetched from the server (the
  // default is DEFAULT_HISTORY_WINDOW_DAYS). Needed both for the
  // History/Completed tabs and the modal's defect-history search.
  useEffect(() => {
    if (!isAuthenticated) return;
    loadCompletedEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, historyWindowDays]);

  // On login, prompt to enable desktop notifications whenever permission has not
  // been granted (i.e. 'default' or 'denied'). Once granted, the check passes
  // and the prompt no longer appears.
  useEffect(() => {
    if (!isAuthenticated) return;
    if (!('Notification' in window)) return;
    if (Notification.permission !== 'granted') setShowNotificationPrompt(true);
  }, [isAuthenticated]);

  // Warm the ATA code cache once authenticated so the modal's Chapter/Section
  // dropdowns are ready instantly on first open. The service caches the result;
  // the modal reads it via its own fetchAtaCodes call.
  useEffect(() => {
    if (!isAuthenticated) return;
    fetchAtaCodes();
  }, [isAuthenticated]);

  // Load the abbreviation list into the runtime store once authenticated so note
  // expansion/casing reflect the admin-managed DynamoDB list (not just the static
  // seed). A failed load leaves the seed in place; edits made in the admin panel
  // refresh the store via the modal's onChanged callback.
  useEffect(() => {
    if (!isAuthenticated) return;
    void refreshAbbreviationStore();
  }, [isAuthenticated]);

  const handleFilterChange = (field: keyof FilterOptions, value: string): void => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleSearchChange = (value: string): void => {
    setSearchQuery(value);
  };

  const handleViewEvent = (event: UnscheduledEvent): void => {
    setModalEvent(event);
    setModalMode('view');
    setIsModalOpen(true);
    hydrateModalEvent(event.eventId);
  };

  const handleEditEvent = (event: UnscheduledEvent): void => {
    // Read-only users can open a record but only in view mode — never edit, and
    // they take no edit lock.
    if (!canEdit) {
      setModalEvent(event);
      setModalMode('view');
      setIsModalOpen(true);
      hydrateModalEvent(event.eventId);
      return;
    }
    // Load the full event while acquiring its lock, then open the modal only
    // after both finish. This prevents projected list data from briefly showing
    // missing defect history and a false required-action indicator.
    Promise.all([acquireEditLock(event.eventId), fetchEventById(event.eventId)])
      .then((result: [AcquireLockResult, UnscheduledEvent | null]): void => {
        const [lockResult, fullEvent] = result;
        if (lockResult.acquired && !fullEvent) {
          void releaseEditLock(event.eventId);
          lockedEventIdRef.current = null;
          logger.error('Full event could not be loaded for editing', { eventId: event.eventId });
          window.alert('Event details could not be loaded. Please retry.');
          reloadActiveEvents();
          return;
        }
        lockedEventIdRef.current = lockResult.acquired ? event.eventId : null;
        setModalEvent(fullEvent || event);
        setModalMode(lockResult.acquired ? 'edit' : 'view');
        setIsModalOpen(true);
        if (!lockResult.acquired) reloadActiveEvents();
      })
      .catch((error: unknown): void => {
        void releaseEditLock(event.eventId);
        lockedEventIdRef.current = null;
        logger.error('Event could not be opened for editing', {
          error: error instanceof Error ? error.message : 'Unknown error',
          eventId: event.eventId,
        });
        window.alert('Event details could not be loaded. Please retry.');
      });
  };

  /**
   * Opens the normal Event modal for a reviewed Triage item. CREATE drafts are
   * pre-populated directly; UPDATE drafts acquire the same edit lock as a normal
   * edit before opening. The backend triage commit is deferred until modal Save.
   */
  const handleOpenTriageEvent = (draft: TriageEventDraft): void => {
    const isCreate: boolean = draft.action === 'CREATE';
    const pending: PendingTriageCommit = {
      triageId: draft.item.triage_id,
      version: draft.item.version,
      action: draft.action,
      ertsAction: draft.ertsAction,
      ertsExistedBefore: draft.ertsExistedBefore,
      recommendationDefectIds: draft.recommendationDefectIds,
      verificationFields: {
        status: isCreate,
        timeDeclaredDate: isCreate && Boolean(draft.item.declaredDate),
        timeDeclaredTime: isCreate && Boolean(draft.item.declaredTimeZulu),
      },
    };
    pendingMoveSourceRef.current = null;
    if (draft.action === 'CREATE') {
      if (!completedHistoryResolved) loadCompletedEvents();
      pendingTriageCommitRef.current = pending;
      setModalEvent(draft.event);
      setModalMode('create');
      setNewRecordType(draft.event.recordType || '');
      setIsModalOpen(true);
      return;
    }
    acquireEditLock(draft.event.eventId).then((result) => {
      if (!result.acquired) {
        logger.warn('Could not acquire edit lock for Triage update', {
          eventId: draft.event.eventId,
        });
        reloadActiveEvents();
        return;
      }
      lockedEventIdRef.current = draft.event.eventId;
      pendingTriageCommitRef.current = pending;
      setModalEvent(draft.event);
      setModalMode('edit');
      setIsModalOpen(true);
    });
  };

  /**
   * Loads the full record (with narrative fields excluded from list views) when
   * a modal opens, then merges it into the open event so View/Edit show notes,
   * summary, ERTS history, etc.
   */
  const hydrateModalEvent = (eventId: string): void => {
    fetchEventById(eventId).then((full) => {
      if (full)
        setModalEvent((current) => (current && current.eventId === eventId ? full : current));
    });
  };

  /** Releases any edit lock this tab holds and refreshes lock ownership. */
  const releaseHeldLock = (): void => {
    const lockedId = lockedEventIdRef.current;
    if (!lockedId) return;
    lockedEventIdRef.current = null;
    releaseEditLock(lockedId).then(() => reloadActiveEvents());
  };

  const handleCloseModal = (): void => {
    releaseHeldLock();
    // Cancelling (including a Triage-staged save or Move create) makes no
    // changes: discard all pending work and leave its source row untouched.
    pendingMoveSourceRef.current = null;
    pendingTriageCommitRef.current = null;
    triageCommitInFlightRef.current = false;
    setIsModalOpen(false);
    setModalEvent(null);
    setNewRecordType('');
  };

  /**
   * Auto-summary for non-Completed records: runs in the background after the
   * record is saved so the user isn't blocked on the Summary tool. Persistence
   * uses the targeted summary endpoint, which cannot replace event or defect
   * data and does not bump the event version, so the current ERTS projection
   * remains valid. Completed records use the explicit Summary button instead.
   */
  const runBackgroundSummary = (record: UnscheduledEvent): void => {
    const notes: string = concatNotesForSummary(record.noteEntries || []);
    if (!notes.trim()) return;
    void generateAndStoreSummaryInBackground(record.eventId, notes, record.summary || '').then(
      (): void => reloadActiveEvents(),
    );
  };

  /**
   * Auto-generates SkyWrench analyses for a saved record's defects in the
   * background. Submits a job per defect that needs one (new, errored, or text
   * changed) and persists the pending result so it appears when the record is
   * next viewed. Skipped for Scheduled records, which never use SkyWrench.
   */
  const runBackgroundSkyWrench = (record: UnscheduledEvent): void => {
    if ((record.recordType || '') === 'Scheduled') return;
    generateSkyWrenchForEvent(record)
      .then(() => reloadActiveEvents())
      .catch((error: unknown) => {
        const message = error instanceof Error ? error.message : 'Unknown error';
        logger.error('Background SkyWrench generation failed', {
          eventId: record.eventId,
          error: message,
        });
      });
  };

  /** Finalizes UI/background work after a successful Triage event commit. */
  const finishTriageCommit = (event: UnscheduledEvent, pending: PendingTriageCommit): void => {
    pendingTriageCommitRef.current = null;
    triageCommitInFlightRef.current = false;
    removeTriageItem(pending.triageId);
    reloadActiveEvents();
    loadCompletedEvents();
    runBackgroundSkyWrench(event);
    releaseHeldLock();
    setIsModalOpen(false);
    setModalEvent(null);
  };

  /** Saves the final EventModal values through the atomic triage commit route. */
  const saveTriageEvent = (
    updated: UnscheduledEvent,
    pending: PendingTriageCommit,
    shouldAutoSummarize: boolean,
  ): void => {
    if (triageCommitInFlightRef.current) return;
    triageCommitInFlightRef.current = true;
    const event: UnscheduledEvent =
      pending.action === 'CREATE'
        ? {
            ...updated,
            eventId: updated.eventId || `evt-${Date.now()}`,
            recordType: newRecordType || updated.recordType || '',
          }
        : updated;
    commitTriageItem(pending.triageId, {
      action: pending.action,
      event,
      version: pending.version,
    }).then((result) => {
      if (result.ok) {
        const committedEvent: UnscheduledEvent = result.event;
        finishTriageCommit(committedEvent, pending);
        if (pending.action === 'UPDATE') requestUpdatedPrediction(committedEvent);
        if (shouldAutoSummarize) runBackgroundSummary(committedEvent);
      } else {
        triageCommitInFlightRef.current = false;
        logger.warn('Triage commit did not succeed', { triageId: pending.triageId });
      }
    });
  };

  /** Creates an event and starts background work against its server-assigned UUID. */
  const saveCreatedEvent = async (
    newEvent: UnscheduledEvent,
    movedSource: UnscheduledEvent | null,
    shouldAutoSummarize: boolean,
  ): Promise<void> => {
    try {
      const created: UnscheduledEvent | null = await createEvent(newEvent);
      if (!created) return;
      if (movedSource) await updateEvent(movedSource);
      reloadActiveEvents();
      if (movedSource) loadCompletedEvents();
      if (shouldAutoSummarize) runBackgroundSummary(created);
      runBackgroundSkyWrench(created);
    } catch (error: unknown) {
      logger.error('Created event follow-up failed', {
        error: error instanceof Error ? error.message : 'Unknown error',
        recordType: newEvent.recordType,
      });
    }
  };

  const showPredictionOutcome = (sequence: number, message: string | null): void => {
    if (latestPredictionStatusRef.current !== sequence) return;
    setPredictionStatus(message);
    if (!message) return;
    window.setTimeout((): void => {
      if (latestPredictionStatusRef.current === sequence) setPredictionStatus(null);
    }, PREDICTION_STATUS_DURATION_MS);
  };

  /** Caches and displays a prediction only for the event revision that produced it. */
  const applyVerifiedPrediction = (
    eventId: string,
    revision: number,
    prediction: ErtsPredictionSummary,
  ): void => {
    const verifiedPrediction: VerifiedPrediction = { revision, prediction };
    verifiedPredictionsRef.current[eventId] = verifiedPrediction;
    window.setTimeout((): void => {
      if (verifiedPredictionsRef.current[eventId] === verifiedPrediction) {
        delete verifiedPredictionsRef.current[eventId];
      }
    }, PROJECTED_ERTS_REFRESH_INTERVAL_MS + 60_000);
    setEvents((current: UnscheduledEvent[]): UnscheduledEvent[] => {
      const currentEvent: UnscheduledEvent | undefined = current.find(
        (event: UnscheduledEvent): boolean => event.eventId === eventId,
      );
      if (!currentEvent || getEventRevision(currentEvent) !== revision) return current;
      return mergeEvents(current, applyProjectedErts(current, { [eventId]: prediction }));
    });
  };

  const requestUpdatedPrediction = (record: UnscheduledEvent): void => {
    if ((record.recordType || 'Unscheduled') !== 'Unscheduled') return;
    if (record.status !== EVENT_STATUS.IN_WORK) return;
    const revision: number = getEventRevision(record);
    const previousVerifiedPrediction: VerifiedPrediction | undefined =
      verifiedPredictionsRef.current[record.eventId];
    if (previousVerifiedPrediction && previousVerifiedPrediction.revision !== revision) {
      delete verifiedPredictionsRef.current[record.eventId];
    }
    const sequence: number = latestPredictionStatusRef.current + 1;
    latestPredictionStatusRef.current = sequence;
    latestPredictionRevisionRef.current[record.eventId] = revision;
    setPredictionStatus(null);
    setPredictionRequestsInFlight((current: number): number => current + 1);
    void requestErtsPrediction(record)
      .then((result: ErtsPredictionRequestResult | null): void => {
        if (latestPredictionRevisionRef.current[record.eventId] !== revision) return;
        if (!result) {
          showPredictionOutcome(sequence, 'Event saved; ERTS projection could not be updated.');
          return;
        }
        applyVerifiedPrediction(record.eventId, revision, result.prediction);
        const skipped: number = result.rejectedDefectCount;
        const message: string | null =
          skipped > 0
            ? `ERTS updated for ${result.servedDefectCount}; ${skipped} defect(s) skipped.`
            : null;
        showPredictionOutcome(sequence, message);
      })
      .finally((): void => {
        setPredictionRequestsInFlight((current: number): number => Math.max(0, current - 1));
      });
  };

  const handleSaveEvent = (updated: UnscheduledEvent): void => {
    const shouldAutoSummarize =
      updated.status !== EVENT_STATUS.COMPLETED &&
      Array.isArray(updated.noteEntries) &&
      updated.noteEntries.length > 0;

    const pendingTriage = pendingTriageCommitRef.current;
    if (pendingTriage) {
      saveTriageEvent(updated, pendingTriage, shouldAutoSummarize);
      return;
    }

    if (modalMode === 'create') {
      const newEvent: UnscheduledEvent = {
        ...updated,
        eventId: updated.eventId || `evt-${Date.now()}`,
        recordType: newRecordType || updated.recordType || '',
      };
      // If this create is the destination of a Move, close the source record
      // (COMPLETED + moveStatus) only after the destination is created so a
      // failed create never leaves an orphaned, already-closed source.
      const movedSource: UnscheduledEvent | null = pendingMoveSourceRef.current;
      pendingMoveSourceRef.current = null;
      void saveCreatedEvent(newEvent, movedSource, shouldAutoSummarize);
    } else {
      // Optimistically reflect the edit in the active list, then sync from the API.
      setEvents((prev) =>
        prev.map((e) => (e.eventId === updated.eventId ? { ...e, ...updated } : e)),
      );
      updateEvent(updated).then((saved: UnscheduledEvent | null): void => {
        reloadActiveEvents();
        loadCompletedEvents();
        if (!saved) return;
        requestUpdatedPrediction(saved);
        if (shouldAutoSummarize) runBackgroundSummary(saved);
        runBackgroundSkyWrench(saved);
      });
    }
    releaseHeldLock();
    setIsModalOpen(false);
    setModalEvent(null);
  };

  /**
   * Moves a record to another record type. The source is closed as COMPLETED
   * (with its `moveStatus` set) via a normal update, then a Create Event window
   * opens pre-populated with the destination record so the user can finalize and
   * create it. The destination carries `movedFrom` so it shows the lineage dot.
   */
  const handleMoveEvent = (source: UnscheduledEvent, destination: UnscheduledEvent): void => {
    // A Move is a separate two-record workflow; it cannot share the atomic
    // single-event Triage commit. Leave the Triage source pending for review.
    pendingTriageCommitRef.current = null;
    triageCommitInFlightRef.current = false;
    // Stage the source close; it is committed only after the destination is
    // created (see handleSaveEvent). Cancelling the Create window discards it.
    pendingMoveSourceRef.current = source;
    releaseHeldLock();

    // Transition the open modal into a pre-populated Create Event window for the
    // destination record type.
    setModalMode('create');
    setNewRecordType(destination.recordType || '');
    setModalEvent(destination);
    setIsModalOpen(true);
  };

  // Heartbeat the held edit lock while the Edit modal stays open so it never
  // lapses mid-edit. Released on close/save; the backend TTL covers tab-close.
  useEffect(() => {
    if (!isModalOpen || modalMode !== 'edit') return undefined;
    const lockedId = lockedEventIdRef.current;
    if (!lockedId) return undefined;
    const intervalId = window.setInterval(() => {
      void refreshEditLock(lockedId);
    }, EDIT_LOCK_HEARTBEAT_INTERVAL_MS);
    return () => window.clearInterval(intervalId);
  }, [isModalOpen, modalMode]);

  const handleNewRecord = (): void => {
    // Only edit-access users can create records.
    if (!canEdit) return;
    // A fresh, unrelated create must not carry staged Move/Triage work.
    pendingMoveSourceRef.current = null;
    pendingTriageCommitRef.current = null;
    triageCommitInFlightRef.current = false;
    setModalEvent(null);
    setModalMode('create');
    setNewRecordType('');
    setIsModalOpen(true);
  };

  // Nav-bar tab changes. History and Scheduled Events are full-width views that
  // don't use the Homepage sidebar, so collapse it when they're selected.
  // Selecting Homepage re-expands the sidebar and restores the last-viewed
  // Homepage (preserved via sidebarTab).
  const handleTabChange = (tab: string): void => {
    activeTabRef.current = tab;
    setActiveTab(tab);
    if (tab === 'triage') setUnseenTriageIds(new Set());
    if (tab === 'history' || tab === 'scheduled-events' || tab === 'triage') {
      setSidebarCollapsed(true);
    } else if (tab === 'homepage') {
      setSidebarCollapsed(false);
    }
  };

  // Sidebar collapse/expand toggle. Expanding returns the user to their
  // last-viewed Homepage rather than leaving a full-width view showing.
  const handleSidebarToggle = (): void => {
    if (sidebarCollapsed) {
      setActiveTab('homepage');
    }
    setSidebarCollapsed((prev) => !prev);
  };

  // Selecting a sidebar tab switches the displayed list. It must also return the
  // main view to 'homepage' — otherwise, after viewing a full-width view such as
  // Completed Events (which sets activeTab='completed-events'), clicking a
  // sidebar tab would only change sidebarTab while the completed view stayed on
  // screen, leaving the user stuck on Completed Events.
  const handleSidebarTabChange = (tab: string): void => {
    setSidebarTab(tab);
    setActiveTab('homepage');
  };

  // Filter events for the Scheduled Events nav tab (all Scheduled IN_WORK or PENDING)
  const scheduledEvents = events.filter((event) => {
    if (event.recordType !== 'Scheduled') return false;
    if (event.status !== EVENT_STATUS.IN_WORK && event.status !== EVENT_STATUS.PENDING)
      return false;
    if (filters.carrier && event.carrier !== filters.carrier) return false;
    if (filters.station && event.station !== filters.station) return false;
    return true;
  });

  // History tab: client-side filtering over the server-loaded completed window.
  // The selected History-window (historyWindowDays) narrows the loaded set to
  // events completed within that many days.
  // Events completed within the selected window. This is the set the History
  // table lists (before categorical filters) and the basis for the faceted
  // filter-option lists, so every offered selection exists in these records.
  // Memoized so the faceted options don't recompute on unrelated re-renders.
  const historyWindowEvents = useMemo(() => {
    const cutoffMs = Date.now() - historyWindowDays * 24 * 60 * 60 * 1000;
    return historyEvents.filter((event) => {
      const completedAt = Date.parse(event.completionTimeZulu);
      return Number.isNaN(completedAt) || completedAt >= cutoffMs;
    });
  }, [historyEvents, historyWindowDays]);
  const filteredHistoryEvents = historyWindowEvents.filter((event) => {
    if (filters.eventType && (event.recordType || 'Unscheduled') !== filters.eventType)
      return false;
    if (filters.carrier && event.carrier !== filters.carrier) return false;
    if (filters.aircraft && event.tailNumber !== filters.aircraft) return false;
    if (filters.station && event.station !== filters.station) return false;
    if (filters.ataChapter && !eventAtaChapters(event).includes(filters.ataChapter)) return false;
    if (filters.irop && isIropCode(filters.irop) && !eventHasIropCode(event, filters.irop))
      return false;
    return true;
  });

  // Filter events for the Planned Events tab
  const plannedEvents = byCarrierThenTail(
    events.filter((event) => {
      if (event.recordType !== 'Planned') return false;
      if (event.status !== EVENT_STATUS.IN_WORK && event.status !== EVENT_STATUS.PENDING)
        return false;
      if (filters.carrier && event.carrier !== filters.carrier) return false;
      if (filters.station && event.station !== filters.station) return false;
      return true;
    }),
  );

  // Filter events for the Deferred Events tab
  const deferredEvents = events.filter((event) => {
    if (event.recordType !== 'Deferred') return false;
    if (event.status !== EVENT_STATUS.IN_WORK && event.status !== EVENT_STATUS.PENDING)
      return false;
    if (filters.carrier && event.carrier !== filters.carrier) return false;
    return true;
  });

  // Completed Events view: Unscheduled records that are COMPLETED with an
  // ERTS/RTS (returnToServiceTimeZulu) date matching the selected RTS Date. The
  // RTS Date defaults to today (Zulu) so the view opens on the current date.
  const rtsDateFilter = filters.rtsDate || todayDateZulu();
  const completedEvents = byCarrierThenTail(
    historyEvents.filter((event) => {
      if ((event.recordType || 'Unscheduled') !== 'Unscheduled') return false;
      if (event.status !== EVENT_STATUS.COMPLETED) return false;
      if (dateZuluOf(event.returnToServiceTimeZulu) !== rtsDateFilter) return false;
      if (filters.carrier && event.carrier !== filters.carrier) return false;
      return true;
    }),
  );

  // Sidebar-driven filter: determines what displays in the main content area when Homepage is active
  const deferredDueState = aggregateDeferredDueState(events);

  // Time-field alert states (Next Advise / ERTS-RTS) for the homepage event types.
  const activeUnscheduled = events.filter(
    (e) =>
      (!e.recordType || e.recordType === 'Unscheduled') &&
      (e.status === EVENT_STATUS.IN_WORK || e.status === EVENT_STATUS.PENDING),
  );
  const unscheduledAlertState = getEventsTimeAlertState(activeUnscheduled);
  const plannedAlertState = getEventsTimeAlertState(plannedEvents);
  const homepageAlertState: TimeAlertState = mergeTimeAlertStates(
    unscheduledAlertState,
    plannedAlertState,
  );

  // Whether any time-based notification is currently flashing across the
  // homepage event types and Deferred Events. Drives the Mute button's
  // visibility (the button only appears while there is something to mute).
  const anyAlertActive = homepageAlertState !== null || deferredDueState !== null;

  // Stable identity for each active alert: `${source}:${eventId}:${severity}`
  // for unscheduled/planned, and an aggregate key for deferred. A new key
  // appearing after a mute (a new event entering alert, or a severity
  // escalation) is treated as a fresh notification that re-enables flashing.
  const alertKeys = new Set<string>();
  const alertNow = new Date();
  for (const event of activeUnscheduled) {
    const state = getEventTimeAlertState(event, alertNow);
    if (state) alertKeys.add(`unscheduled:${event.eventId}:${state}`);
  }
  for (const event of plannedEvents) {
    const state = getEventTimeAlertState(event, alertNow);
    if (state) alertKeys.add(`planned:${event.eventId}:${state}`);
  }
  if (deferredDueState) alertKeys.add(`deferred:${deferredDueState}`);

  // A new notification appeared after muting if the current alert set contains
  // a key that was not present when the user muted. Cleared alerts do not
  // re-enable flashing — only genuinely new ones do.
  const hasUnmutedAlert =
    alertsMuted && Array.from(alertKeys).some((key) => !mutedAlertKeys.has(key));
  useEffect(() => {
    if (hasUnmutedAlert) setAlertsMuted(false);
  }, [hasUnmutedAlert]);

  /** Mutes the currently active alerts: flashing stops (color is retained). */
  const handleMuteAlerts = (): void => {
    setMutedAlertKeys(new Set(alertKeys));
    setAlertsMuted(true);
  };

  // Flash the browser tab (favicon + title) red whenever the Homepage button is
  // flashing. Note: a web app cannot flash the OS taskbar/tray icon directly;
  // the hook raises a Web Notification when hidden as the closest equivalent.
  useAlertFavicon(homepageAlertState !== null);

  const sidebarFilteredEvents = (() => {
    const filtered = getSidebarFilteredEvents(events, sidebarTab, filters, Date.now());
    // Preserve prior ordering: unscheduled/planned are carrier-then-tail sorted;
    // deferred/scheduled keep their natural (table-controlled) order.
    return sidebarTab === 'unscheduled' || sidebarTab === 'planned'
      ? byCarrierThenTail(filtered)
      : filtered;
  })();

  const selectedRecordType: string | undefined = CLEARABLE_RECORD_TYPES[sidebarTab];
  const clearableEvents: UnscheduledEvent[] = events.filter((event: UnscheduledEvent): boolean => {
    const recordType: string = event.recordType || 'Unscheduled';
    const isActive: boolean =
      event.status === EVENT_STATUS.IN_WORK || event.status === EVENT_STATUS.PENDING;
    return recordType === selectedRecordType && isActive;
  });

  /** Marks every active event in the selected homepage section as Deleted. */
  const handleClearAllEvents = async (): Promise<void> => {
    if (!canEdit || clearAllBusy || !selectedRecordType || clearableEvents.length === 0) return;
    const confirmed: boolean = window.confirm(
      `Mark all ${clearableEvents.length} active ${selectedRecordType} events as Deleted?`,
    );
    if (!confirmed) return;

    setClearAllBusy(true);
    try {
      const result: ClearActiveEventsResult = await clearActiveEvents(clearableEvents);
      const deletedIds: Set<string> = new Set<string>(result.deletedEventIds);
      setEvents((current: UnscheduledEvent[]): UnscheduledEvent[] =>
        current.filter((event: UnscheduledEvent): boolean => !deletedIds.has(event.eventId)),
      );
      logger.info('Clear All completed', {
        recordType: selectedRecordType,
        deletedCount: result.deletedEventIds.length,
        failedCount: result.failedEventIds.length,
      });
      if (result.failedEventIds.length > 0) {
        window.alert(`${result.failedEventIds.length} events could not be cleared. Please retry.`);
      }
      reloadActiveEvents();
    } catch (error: unknown) {
      logger.error('Clear All failed', {
        error: error instanceof Error ? error.message : 'Unknown error',
        recordType: selectedRecordType,
      });
      window.alert('Events could not be cleared. Please retry.');
    } finally {
      setClearAllBusy(false);
    }
  };

  // Tail Number options for the non-History tabs (active + history tails).
  const tailNumberOptions = useMemo(
    () => Array.from(new Set([...events, ...historyEvents].map((e) => e.tailNumber))).sort(),
    [events, historyEvents],
  );
  // Faceted History filter options: each dropdown lists only the values present
  // in the records within the selected window that pass the other active
  // filters, so every offered selection exists in the listed records. Recomputed
  // when the window set or the active filters change.
  const historyFilterOptions = useMemo(
    () => computeHistoryFilterOptions(historyWindowEvents, filters),
    [historyWindowEvents, filters],
  );

  const activeModalEvent: UnscheduledEvent | undefined = modalEvent
    ? events.find((event: UnscheduledEvent): boolean => event.eventId === modalEvent.eventId)
    : undefined;
  const modalProjectedErts: Record<number, string> | undefined = activeModalEvent
    ? (activeModalEvent.projectedErts ?? {})
    : undefined;

  if (isLoading) {
    return <div className="loading-screen">Loading...</div>;
  }

  if (!isAuthenticated) {
    return <LoginScreen />;
  }

  return (
    <>
      <Header
        canManageAbbreviations={canManageAbbreviations}
        onManageAbbreviations={() => setIsAbbreviationsOpen(true)}
      />
      <NavBar
        activeTab={activeTab}
        onTabChange={handleTabChange}
        onNewRecord={handleNewRecord}
        homepageAlertClass={alertFlashClass(homepageAlertState, alertsMuted)}
        triagePendingCount={triageIsMock ? 0 : triageItems.length}
        triageHasUnseen={!triageIsMock && unseenTriageIds.size > 0}
        canEdit={canEdit}
      />
      <div className="app-layout">
        <aside
          className={`app-sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}
          role="navigation"
          aria-label="Side navigation"
        >
          <button
            className={`sidebar-toggle ${alertFlashClass(homepageAlertState, alertsMuted)}`}
            type="button"
            onClick={handleSidebarToggle}
            aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {sidebarCollapsed ? '▶' : '◀'}
          </button>
          <button
            className={`sidebar-link ${sidebarTab === 'unscheduled' ? 'active' : ''} ${alertFlashClass(unscheduledAlertState, alertsMuted)}`}
            type="button"
            onClick={() => handleSidebarTabChange('unscheduled')}
          >
            Unscheduled Events
          </button>
          <button
            className={`sidebar-link ${sidebarTab === 'planned' ? 'active' : ''} ${alertFlashClass(plannedAlertState, alertsMuted)}`}
            type="button"
            onClick={() => handleSidebarTabChange('planned')}
          >
            Planned Events
          </button>
          <button
            className={`sidebar-link ${sidebarTab === 'deferred' ? 'active' : ''} ${deferredFlashClass(deferredDueState, alertsMuted)}`}
            type="button"
            onClick={() => handleSidebarTabChange('deferred')}
          >
            Deferred Events
          </button>
          <button
            className={`sidebar-link ${sidebarTab === 'scheduled' ? 'active' : ''}`}
            type="button"
            onClick={() => handleSidebarTabChange('scheduled')}
          >
            Scheduled Events
          </button>
          <button
            className="sidebar-link"
            type="button"
            onClick={() => setActiveTab('completed-events')}
          >
            Completed Events
          </button>
          {anyAlertActive && !alertsMuted && (
            <button
              className="sidebar-link sidebar-mute-btn"
              type="button"
              onClick={handleMuteAlerts}
              aria-label="Mute flashing alerts"
              title="Mute flashing alerts"
            >
              <img
                src={`${process.env.PUBLIC_URL}/mute-button.png`}
                alt=""
                aria-hidden="true"
                className="sidebar-mute-img"
              />
            </button>
          )}
          <div className="sidebar-badge">
            <picture>
              <source srcSet={mxoDevLogo} type="image/webp" />
              <img src={mxoDevLogoGif} alt="MXO-DEV" className="sidebar-badge-img" />
            </picture>
          </div>
        </aside>
        <div className="app-main">
          {(isRefreshing || predictionRequestsInFlight > 0 || predictionStatus) && (
            <div className="refresh-indicator" role="status" aria-live="polite">
              {(isRefreshing || predictionRequestsInFlight > 0) && (
                <span className="refresh-spinner" aria-hidden="true" />
              )}
              <span className="refresh-indicator-text">
                {predictionStatus ||
                  (predictionRequestsInFlight > 0 ? 'Updating ERTS…' : 'Updating…')}
              </span>
            </div>
          )}
          {activeTab !== 'triage' && (
            <FilterBar
              filters={filters}
              searchQuery={searchQuery}
              onFilterChange={handleFilterChange}
              onSearchChange={handleSearchChange}
              showClearAll={
                CLEAR_ALL_ENABLED && activeTab === 'homepage' && selectedRecordType !== undefined
              }
              clearAllDisabled={!canEdit || clearableEvents.length === 0}
              clearAllBusy={clearAllBusy}
              onClearAll={handleClearAllEvents}
              showRecordType={activeTab === 'history'}
              hideStatus={activeTab === 'history'}
              showAtaCode={activeTab === 'history'}
              tailNumberOptions={
                activeTab === 'history' ? historyFilterOptions.tailNumbers : tailNumberOptions
              }
              carrierOptions={activeTab === 'history' ? historyFilterOptions.carriers : undefined}
              stationOptions={activeTab === 'history' ? historyFilterOptions.stations : undefined}
              recordTypeOptions={
                activeTab === 'history' ? historyFilterOptions.recordTypes : undefined
              }
              ataChapterOptions={
                activeTab === 'history' ? historyFilterOptions.ataChapters : undefined
              }
              iropOptions={activeTab === 'history' ? historyFilterOptions.iropOptions : undefined}
              showRtsDate={activeTab === 'completed-events'}
              showHistoryWindow={activeTab === 'history'}
              historyWindowDays={historyWindowDays}
              historyWindowOptions={[...HISTORY_WINDOW_OPTIONS]}
              onHistoryWindowChange={setHistoryWindowDays}
            />
          )}
          {TRIAGE_ENABLED && activeTab === 'triage' && (
            <TriageSection
              items={triageItems}
              isMock={triageIsMock}
              loading={triageLoading}
              canEdit={canEdit}
              allowClearAll={CLEAR_ALL_ENABLED}
              onOpenEvent={handleOpenTriageEvent}
              onRemoveItem={removeTriageItem}
            />
          )}
          {activeTab === 'homepage' && sidebarTab !== 'deferred' && (
            <EventsTable
              events={sidebarFilteredEvents}
              onView={handleViewEvent}
              onEdit={handleEditEvent}
              hideProjectedErts={sidebarTab === 'planned'}
              lockOwners={lockOwners}
              currentUserAlias={currentUserAlias}
              flagIncompleteDamageLocation
            />
          )}
          {activeTab === 'homepage' && sidebarTab === 'deferred' && (
            <DeferredTable
              events={sidebarFilteredEvents}
              onView={handleViewEvent}
              onEdit={handleEditEvent}
              lockOwners={lockOwners}
              currentUserAlias={currentUserAlias}
            />
          )}
          {activeTab === 'scheduled-events' && (
            <ScheduledTable
              events={scheduledEvents}
              onView={handleViewEvent}
              onEdit={handleEditEvent}
              lockOwners={lockOwners}
              currentUserAlias={currentUserAlias}
            />
          )}
          {activeTab === 'planned-events' && (
            <EventsTable
              events={plannedEvents}
              onView={handleViewEvent}
              onEdit={handleEditEvent}
              hideProjectedErts
              lockOwners={lockOwners}
              currentUserAlias={currentUserAlias}
            />
          )}
          {activeTab === 'deferred-events' && (
            <DeferredTable
              events={deferredEvents}
              onView={handleViewEvent}
              onEdit={handleEditEvent}
              lockOwners={lockOwners}
              currentUserAlias={currentUserAlias}
            />
          )}
          {activeTab === 'completed-events' && (
            <EventsTable
              events={completedEvents}
              onView={handleViewEvent}
              onEdit={handleEditEvent}
              hideTimeAlerts
              lockOwners={lockOwners}
              currentUserAlias={currentUserAlias}
            />
          )}
          {activeTab === 'history' && (
            <EventsTable
              events={filteredHistoryEvents}
              onView={handleViewEvent}
              onEdit={handleEditEvent}
              showRecordType
              hideTimeAlerts
              pageSize={HISTORY_PAGE_SIZE}
              lockOwners={lockOwners}
              currentUserAlias={currentUserAlias}
              flagIncompleteDamageLocation
            />
          )}
        </div>
      </div>
      <EventModal
        event={modalEvent}
        mode={modalMode}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSave={handleSaveEvent}
        onMove={handleMoveEvent}
        recordType={newRecordType}
        onRecordTypeChange={setNewRecordType}
        events={historyEvents}
        completedHistoryResolved={completedHistoryResolved}
        activeEvents={events}
        projectedErtsOverlay={modalProjectedErts}
        triageErtsAction={pendingTriageCommitRef.current?.ertsAction}
        triageErtsExistedBefore={pendingTriageCommitRef.current?.ertsExistedBefore}
        hasStagedTriageChanges={pendingTriageCommitRef.current?.action === 'UPDATE'}
        triageRecommendationDefectIds={pendingTriageCommitRef.current?.recommendationDefectIds}
        verificationFields={pendingTriageCommitRef.current?.verificationFields}
      />
      <NotificationPrompt
        isOpen={showNotificationPrompt}
        blocked={'Notification' in window && Notification.permission === 'denied'}
        onAccept={() => {
          if ('Notification' in window) {
            Notification.requestPermission().finally(() => setShowNotificationPrompt(false));
          } else {
            setShowNotificationPrompt(false);
          }
        }}
        onCancel={() => setShowNotificationPrompt(false)}
      />
      <AbbreviationsModal
        isOpen={isAbbreviationsOpen}
        onClose={() => setIsAbbreviationsOpen(false)}
        onChanged={() => {
          void refreshAbbreviationStore();
        }}
      />
      <div className="dev-credit">Developed by MXO-DEV</div>
    </>
  );
};

export default App;
